import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AcoesDaLinha, BotaoVer, BotaoEditar, BotaoExcluir } from '../../components/BotoesAcao';
import { useConfirm } from '../../components/ConfirmDialog';
import { Motorista } from '../../types';
import { motoristasService, MotoristasResponse } from '../../services/motoristas';
import ExportButtons from '../../components/ExportButtons';
import { exportService } from '../../services/export';
import { parseApiDate } from '../../utils/formatters';
import { usePermissoes } from '../../hooks/usePermissoes';
import AvisoSemPermissao from '../../components/AvisoSemPermissao';
import { ehSemPermissao, mensagemDoErro } from '../../utils/erros';

const MotoristasList: React.FC = () => {
  const { pode } = usePermissoes();
  const [filters, setFilters] = useState({
    status: '',
    categoria_cnh: '',
    cidade: '',
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    per_page: 15,
    page: 1
  });

  const navigate = useNavigate();
  const confirm = useConfirm();

  const { data, isLoading, error, refetch } = useQuery<MotoristasResponse>({
    queryKey: ['motoristas', filters],
    queryFn: () => motoristasService.getMotoristas(filters),
    placeholderData: (previousData) => previousData
  });

  const handleDelete = async (motorista: Motorista) => {
    const ok = await confirm({
      title: 'Excluir motorista',
      message: `Tem certeza que deseja excluir o motorista ${motorista.nome}? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await motoristasService.deleteMotorista(motorista.id);
      toast.success('Motorista excluído com sucesso');
      refetch();
    } catch (err) {
      toast.error(mensagemDoErro(err, 'Erro ao excluir motorista'));
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    // Voltar para a primeira pagina ao mudar um filtro esta certo: o resultado
    // e outro e a pagina 5 do resultado anterior nao significa nada. Mas isso
    // nao pode valer quando a chave alterada E a pagina, senao o botao Proxima
    // pede a pagina 2 e a linha seguinte devolve para a 1 — a paginacao fica
    // inutilizada sem nenhum sinal de erro.
    setFilters(prev => ({ ...prev, [key]: value, ...(key === 'page' ? {} : { page: 1 }) }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800';
      case 'inativo': return 'bg-gray-100 text-gray-800';
      case 'suspenso': return 'bg-red-100 text-red-800';
      case 'licenciado': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCnhStatusColor = (vencimento: string) => {
    const hoje = new Date();
    const vencimentoDate = new Date(vencimento);
    const diasAteVencimento = Math.ceil((vencimentoDate.getTime() - hoje.getTime()) / (1000 * 3600 * 24));
    
    if (diasAteVencimento < 0) return 'bg-red-500';
    if (diasAteVencimento <= 30) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const formatCpf = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatDate = (date: string) => {
    const d = parseApiDate(date);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  // Tratar erros
  useEffect(() => {
    // Falta de permissão não vira aviso passageiro: a tela inteira explica o
    // que aconteceu, logo abaixo, e um toast só somaria ruído.
    if (error && !ehSemPermissao(error)) {
      toast.error(mensagemDoErro(error, 'Erro ao carregar motoristas'));
    }
  }, [error]);

  if (ehSemPermissao(error)) {
    return <AvisoSemPermissao erro={error} />;
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Motoristas</h1>
          <p className="text-gray-600">Gerencie sua equipe de motoristas</p>
        </div>
        <div className="flex items-center gap-3">
          {pode('relatorios', 'exportar') && (
            <ExportButtons
            onExportPdf={() => exportService.motoristasPdf()}
            onExportExcel={() => exportService.motoristasExcel()}
          />
          )}
          {pode('motoristas', 'criar') && (
          <Link
            to="/motoristas/novo"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo motorista
          </Link>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="suspenso">Suspenso</option>
              <option value="licenciado">Licenciado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria CNH</label>
            <select
              value={filters.categoria_cnh}
              onChange={(e) => handleFilterChange('categoria_cnh', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
              <option value="E">E</option>
              <option value="AB">AB</option>
              <option value="AC">AC</option>
              <option value="AD">AD</option>
              <option value="AE">AE</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
            <input
              type="text"
              value={filters.cidade}
              onChange={(e) => handleFilterChange('cidade', e.target.value)}
              placeholder="Digite a cidade..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Nome, CPF ou CNH..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Lista de Motoristas */}
      <div className="bg-white rounded-lg shadow-sm border">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando motoristas...</p>
          </div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Motorista
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNH
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Localização
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.data.map((motorista: Motorista) => (
                    <tr key={motorista.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {motorista.nome}
                          </div>
                          <div className="text-sm text-gray-500">
                            CPF: {formatCpf(motorista.cpf)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {motorista.idade ? motorista.idade() : new Date().getFullYear() - new Date(motorista.data_nascimento).getFullYear()} anos • {motorista.estado_civil}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(motorista.status)}`}>
                          {motorista.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {motorista.cnh} • {motorista.categoria_cnh}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${getCnhStatusColor(motorista.vencimento_cnh)}`}></span>
                            <span className="text-sm text-gray-500">
                              Vence: {formatDate(motorista.vencimento_cnh)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {motorista.celular}
                        </div>
                        {motorista.telefone && (
                          <div className="text-sm text-gray-500">
                            {motorista.telefone}
                          </div>
                        )}
                        {motorista.email && (
                          <div className="text-sm text-gray-500">
                            {motorista.email}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {motorista.cidade}, {motorista.estado}
                        </div>
                        <div className="text-sm text-gray-500">
                          {motorista.bairro}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <AcoesDaLinha>
                          <BotaoVer onClick={() => navigate(`/motoristas/${motorista.id}`)} title="Ver detalhes" />
                          {pode('motoristas', 'editar') && <BotaoEditar onClick={() => navigate(`/motoristas/${motorista.id}/editar`)} />}
                          {pode('motoristas', 'excluir') && <BotaoExcluir onClick={() => handleDelete(motorista)} />}
                        </AcoesDaLinha>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {data.pagination && data.pagination.last_page > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page - 1)}
                    disabled={data.pagination.current_page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page + 1)}
                    disabled={data.pagination.current_page === data.pagination.last_page}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((data.pagination.current_page - 1) * data.pagination.per_page) + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(data.pagination.current_page * data.pagination.per_page, data.pagination.total)}
                      </span> de{' '}
                      <span className="font-medium">{data.pagination.total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page - 1)}
                        disabled={data.pagination.current_page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page + 1)}
                        disabled={data.pagination.current_page === data.pagination.last_page}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum motorista encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece criando um novo motorista.
            </p>
            <div className="mt-6">
              <Link
                to="/motoristas/novo"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Novo motorista
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MotoristasList;
